import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Segmented,
  Descriptions,
  Button,
  Form,
  Input,
  Space,
  Divider,
  Modal,
  message,
  Tag,
  Spin,
  theme,
} from "antd";
import {
  DollarOutlined,
  BankOutlined,
  DownloadOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  FileTextOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import api from "@/libs/api";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

export default function AccountSummary() {
  const { token } = theme.useToken();
  const [viewMode, setViewMode] = useState("Monthly");
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [companyProfile, setCompanyProfile] = useState({});
  const [companyId, setCompanyId] = useState(null);
  const [vaultData, setVaultData] = useState(null);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [form] = Form.useForm();

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  // Fetch current user profile to get companyId
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/user/profile`, { headers: authHeaders() });
        const result = await resp.json();
        if (result.r === 0 && result.d) {
          setCompanyId(result.d.companyId || result.d.company_id);
          setCompanyProfile({
            companyName: result.d.companyName || result.d.company_name || "",
            abn: result.d.companyAbn || result.d.company_abn || "",
            contactName: result.d.name || "",
            contactEmail: result.d.email || "",
            contactPhone: result.d.phone || "",
            address: result.d.companyAddress || result.d.company_addressline || "",
          });
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };
    fetchProfile();
  }, [authHeaders]);

  // Fetch vault (masked bank details) when companyId is known
  useEffect(() => {
    if (!companyId) return;
    const fetchVault = async () => {
      setVaultLoading(true);
      try {
        const resp = await fetch(`${API_BASE}/api/vault/company/${companyId}`, { headers: authHeaders() });
        const result = await resp.json();
        if (result.r === 0 && result.d) {
          setVaultData(result.d);
        } else {
          setVaultData(null);
        }
      } catch (err) {
        console.error("Error fetching vault:", err);
        setVaultData(null);
      } finally {
        setVaultLoading(false);
      }
    };
    fetchVault();
  }, [companyId, authHeaders]);

  // Fetch account summary (settlement rules + ledger) when companyId is known
  useEffect(() => {
    if (!companyId) return;
    const fetchSummary = async () => {
      try {
        const res = await api.get(`/api/companies/${companyId}/account-summary`);
        if (res.data.success && res.data.data) {
          setAccountData(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching account summary:", err);
      }
    };
    fetchSummary();
  }, [companyId]);

  // Chart data from API or empty
  const getChartData = () => {
    if (!accountData?.monthlyLedger?.length) return [];
    const ledger = [...accountData.monthlyLedger].reverse(); // API returns DESC

    if (viewMode === "Yearly") {
      // Aggregate months into years
      const yearMap = {};
      for (const row of ledger) {
        const year = row.month.substring(0, 4);
        if (!yearMap[year]) yearMap[year] = { year, receivables: 0, payments: 0 };
        yearMap[year].receivables += row.receivables;
        yearMap[year].payments += row.payments;
      }
      return Object.values(yearMap).sort((a, b) => a.year.localeCompare(b.year));
    }

    // Monthly: format month labels
    return ledger.map((row) => ({
      month: dayjs(row.month + "-01").format("MMM YYYY"),
      receivables: row.receivables,
      payments: row.payments,
    }));
  };

  const chartData = getChartData();
  const xAxisKey = viewMode === "Yearly" ? "year" : "month";

  // Totals from API
  const totalReceivables = accountData?.totals?.totalReceivables || 0;
  const totalPayments = accountData?.totals?.totalPayments || 0;
  const pendingAmount = accountData?.totals?.pendingAmount || 0;

  // Revenue sharing from API
  const revenueSharing = accountData?.revenueSharing;
  const companyCapacity = accountData?.capacity;

  const handleEditProfile = () => {
    form.setFieldsValue({
      ...companyProfile,
      bankName: "",
      bsb: "",
      accountNumber: "",
      accountName: "",
    });
    setIsEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    try {
      const values = await form.validateFields();
      const { bankName, bsb, accountNumber, accountName, ...profileValues } = values;

      // Update profile state (company info portion)
      setCompanyProfile((prev) => ({ ...prev, ...profileValues }));

      // Save bank details to vault if any bank field is filled
      if (bsb && accountNumber && companyId) {
        const resp = await fetch(`${API_BASE}/api/vault`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            ownerType: "company",
            ownerId: companyId,
            bsb: bsb.replace(/[-\s]/g, ""),
            accountNumber: accountNumber.replace(/\s/g, ""),
            accountName: accountName || null,
            bankName: bankName || null,
          }),
        });
        const result = await resp.json();
        if (result.r === 0) {
          // Refresh vault data
          setVaultData({
            vaultId: result.d.vaultId,
            bankName: bankName || null,
            bsbLast2: result.d.bsbLast2,
            accountLast2: result.d.accountLast2,
            status: "active",
          });
          message.success("Bank details saved securely");
        } else {
          message.error("Failed to save bank details");
          return;
        }
      }

      setIsEditingProfile(false);
      message.success("Company profile updated");
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleCancelEdit = () => {
    form.resetFields();
    setIsEditingProfile(false);
  };

  const handleDownloadContract = () => {
    message.info("Downloading contract...");
    // In a real app, this would trigger a file download
  };

  return (
    <PageLayout active="accountSummary">
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>Account Summary</Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Overview of your account financials, company profile, and partnership details
        </Text>
      </div>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
            <Statistic
              title="Total Receivables"
              value={totalReceivables}
              precision={2}
              prefix={<DollarOutlined style={{ color: token.colorPrimary }} />}
              valueStyle={{ color: token.colorPrimary, fontWeight: 600 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Since partnership started
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
            <Statistic
              title="Total Payments Received"
              value={totalPayments}
              precision={2}
              prefix={<DollarOutlined style={{ color: token.colorSuccess }} />}
              valueStyle={{ color: token.colorSuccess, fontWeight: 600 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              All-time payments
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
            <Statistic
              title="Pending Amount"
              value={pendingAmount}
              precision={2}
              prefix={<DollarOutlined style={{ color: token.colorWarning }} />}
              valueStyle={{ color: token.colorWarning, fontWeight: 600 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Outstanding balance
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Receivables vs Payments Chart */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 24 }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Receivables vs Payments</span>
            <Segmented
              options={["Monthly", "Yearly"]}
              value={viewMode}
              onChange={setViewMode}
              size="small"
            />
          </div>
        }
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: "12px 16px",
            minHeight: 48,
          },
          body: { padding: 16 },
        }}
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
            <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} stroke={token.colorTextSecondary} />
            <YAxis tick={{ fontSize: 12 }} stroke={token.colorTextSecondary} />
            <Tooltip
              contentStyle={{
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: 8,
              }}
              formatter={(value) => [`$${value.toLocaleString()}`, ""]}
            />
            <Legend />
            <Bar dataKey="receivables" name="Receivables" fill={token.colorPrimary} radius={[4, 4, 0, 0]} />
            <Bar dataKey="payments" name="Payments" fill={token.colorSuccess} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Row gutter={[16, 16]}>
        {/* Company Profile */}
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, height: "100%" }}
            title={
              <Space>
                <BankOutlined style={{ color: token.colorPrimary }} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>Company Profile</span>
              </Space>
            }
            extra={
              !isEditingProfile ? (
                <Button type="link" icon={<EditOutlined />} onClick={handleEditProfile}>
                  Edit
                </Button>
              ) : (
                <Space>
                  <Button icon={<CloseOutlined />} onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveProfile}>
                    Save
                  </Button>
                </Space>
              )
            }
            styles={{
              header: {
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                padding: "12px 16px",
                minHeight: 48,
              },
              body: { padding: 16 },
            }}
          >
            {!isEditingProfile ? (
              <Descriptions column={1} size="small" labelStyle={{ width: 140 }}>
                <Descriptions.Item label="Company Name">{companyProfile.companyName}</Descriptions.Item>
                <Descriptions.Item label="ABN">{companyProfile.abn}</Descriptions.Item>
                <Descriptions.Item label="Contact Name">{companyProfile.contactName}</Descriptions.Item>
                <Descriptions.Item label="Email">{companyProfile.contactEmail}</Descriptions.Item>
                <Descriptions.Item label="Phone">{companyProfile.contactPhone}</Descriptions.Item>
                <Descriptions.Item label="Address">{companyProfile.address}</Descriptions.Item>
                <Descriptions.Item label="Bank Name">
                  {vaultLoading ? <Spin size="small" /> : vaultData?.bankName || <Text type="secondary">Not set</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="BSB">
                  {vaultLoading ? <Spin size="small" /> : vaultData ? <Text code>****{vaultData.bsbLast2}</Text> : <Text type="secondary">Not set</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Account Number">
                  {vaultLoading ? <Spin size="small" /> : vaultData ? <Text code>******{vaultData.accountLast2}</Text> : <Text type="secondary">Not set</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Account Name"><Text type="secondary">Encrypted</Text></Descriptions.Item>
              </Descriptions>
            ) : (
              <Form form={form} layout="vertical" size="small">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="companyName" label="Company Name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="abn" label="ABN" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="contactName" label="Contact Name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="contactEmail" label="Email" rules={[{ required: true, type: "email" }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="contactPhone" label="Phone">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="address" label="Address">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider style={{ margin: "12px 0" }} />
                <Text strong style={{ display: "block", marginBottom: 12 }}>Bank Details</Text>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="bankName" label="Bank Name">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="bsb" label="BSB">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="accountNumber" label="Account Number">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="accountName" label="Account Name">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            )}
          </Card>
        </Col>

        {/* Revenue Sharing */}
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, height: "100%" }}
            title={
              <Space>
                <PercentageOutlined style={{ color: token.colorPrimary }} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>Revenue Sharing</span>
              </Space>
            }
            extra={
              <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadContract}>
                Download Contract
              </Button>
            }
            styles={{
              header: {
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                padding: "12px 16px",
                minHeight: 48,
              },
              body: { padding: 16 },
            }}
          >
            {revenueSharing ? (
              <>
                <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Rule">
                    <Tag color="blue">{revenueSharing.ruleName}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Type">
                    <Tag color="cyan">{revenueSharing.ruleCode}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Effective From">
                    {revenueSharing.effectiveFrom ? dayjs(revenueSharing.effectiveFrom).format("YYYY-MM-DD") : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Effective To">
                    {revenueSharing.effectiveTo ? dayjs(revenueSharing.effectiveTo).format("YYYY-MM-DD") : "Ongoing"}
                  </Descriptions.Item>
                  {revenueSharing.params?.revenue_share_pct != null && (
                    <Descriptions.Item label="Revenue Share">
                      <Tag color="green">{revenueSharing.params.revenue_share_pct}%</Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                <Divider style={{ margin: "12px 0" }} />

                {companyCapacity && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <Text strong>Onboarded Capacity</Text>
                    <Space>
                      <Tag color="blue">{companyCapacity.totalMw.toFixed(2)} MW</Tag>
                      <Tag color="purple">{companyCapacity.totalKwh.toLocaleString()} kWh</Tag>
                      <Tag>{companyCapacity.systemCount} systems</Tag>
                    </Space>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: 24, color: token.colorTextSecondary }}>
                No revenue sharing rules configured
              </div>
            )}

            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: token.colorFillQuaternary,
                borderRadius: 8,
              }}
            >
              <Space>
                <FileTextOutlined style={{ color: token.colorTextSecondary }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  For detailed terms and conditions, please download and review the full contract document.
                </Text>
              </Space>
            </div>
          </Card>
        </Col>
      </Row>
    </PageLayout>
  );
}
